import React, { useEffect, useRef, useState } from 'react';

export type UseReadMoreOptions = {
  /** Maximum number of lines to show when collapsed */
  maxLines?: number;
  /** Estimated characters per line for fallback calculation */
  estimatedLineLength?: number;
  /** Custom threshold for determining if content should be truncated */
  characterThreshold?: number;
};

export type UseReadMoreReturn = {
  /** Ref to attach to the content element */
  contentRef: React.RefObject<HTMLDivElement>;
  /** Whether content is currently expanded */
  isExpanded: boolean;
  /** Whether the read more/less button should be shown */
  shouldShowButton: boolean;
  /** Whether JavaScript enhancement has been applied */
  isEnhanced: boolean;
  /** Function to toggle expanded state */
  toggle: () => void;
  /** Props to spread on the content element for progressive enhancement */
  contentProps: {
    'data-js-enhanced': string;
    'data-expanded': string;
    style: React.CSSProperties & {
      '--line-clamp': number;
    };
  };
};

/**
 * useReadMore Hook
 *
 * Provides progressive enhancement for read more/less functionality.
 * Content starts fully visible (SEO/accessibility friendly) and JavaScript
 * applies truncation after determining if content overflows.
 *
 * Key features:
 * - Progressive enhancement (works without JavaScript)
 * - SEO-friendly (content visible by default)
 * - Accessible (screen readers get full content)
 * - Smart overflow detection
 * - Customizable line limits and thresholds
 *
 * @param content The text content to potentially truncate
 * @param options Configuration options for truncation behavior
 * @returns Object with state, refs, handlers, and props for content element
 */
export const useReadMore = (
  content: string | null | undefined,
  options: UseReadMoreOptions = {},
): UseReadMoreReturn => {
  const { maxLines = 2, estimatedLineLength = 75, characterThreshold = undefined } = options;

  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate default character threshold based on maxLines and estimatedLineLength
  const defaultCharacterThreshold = maxLines * estimatedLineLength;
  const finalCharacterThreshold = characterThreshold ?? defaultCharacterThreshold;

  // Progressive enhancement effect - runs after component mounts
  useEffect(() => {
    if (!content) {
      setShouldShowButton(false);
      return;
    }

    // Set enhanced state immediately to apply CSS data attributes
    setIsEnhanced(true);

    // Use requestAnimationFrame to ensure DOM has updated with data attributes
    requestAnimationFrame(() => {
      if (contentRef.current) {
        const element = contentRef.current;

        // First check: DOM-based overflow detection (most accurate)
        const isOverflowingByHeight = element.scrollHeight > element.clientHeight;

        if (isOverflowingByHeight) {
          setShouldShowButton(true);
        } else {
          // Second check: Character count fallback
          // This helps when DOM measurements aren't reliable (tests, SSR, etc.)
          const isOverflowingByCharCount = content.length > finalCharacterThreshold;
          setShouldShowButton(isOverflowingByCharCount);
        }
      } else {
        // Fallback: No DOM element available, use character count only
        setShouldShowButton(content.length > finalCharacterThreshold);
      }
    });
  }, [content, finalCharacterThreshold, maxLines]);

  // Reset expansion state when content changes
  useEffect(() => {
    setIsExpanded(false);
  }, [content]);

  const toggle = () => {
    setIsExpanded(!isExpanded);
  };

  return {
    contentRef,
    isExpanded,
    shouldShowButton,
    isEnhanced,
    toggle,
    contentProps: {
      'data-js-enhanced': isEnhanced ? 'true' : 'false',
      'data-expanded': isExpanded ? 'true' : 'false',
      style: {
        '--line-clamp': maxLines,
      },
    },
  };
};
